export const convertHtmlToRawContentState = (controlValue) => {
  const html = controlValue.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
  const isBlockTag = checkBlockTag(html);
  const value = isBlockTag ? html : `<div>${html}</div>`;
  const parser = new DOMParser();
  const doc = parser.parseFromString(value, 'text/html');
  const rawContentState = { blocks: [], entityMap: {} };

  const getInlineStyles = (element) => {
    const styles = [];
    const style = element.style;

    if (style.color) {
      const isRGBValue = style.color.includes('rgb');
      styles.push(`color-${isRGBValue ? rgbToHex(style.color) : style.color}`);
    }
    if (style.backgroundColor) {
      const isRGBValue = style.backgroundColor.includes('rgb');
      styles.push(
        `backgroundColor-${isRGBValue ? rgbToHex(style.backgroundColor) : style.backgroundColor}`
      );
    }
    if (style.fontFamily) {
      styles.push(`fontFamily-${style.fontFamily.replace(/[\\"]/g, '')}`);
    }
    if (style.fontSize) {
      styles.push(`fontSize-${style.fontSize}`);
    }
    if (style.textTransform) {
      styles.push(style.textTransform === 'lowercase' ? 'LOWERCASE' : 'UPPERCASE');
    }
    if (style.fontWeight || ['B', 'STRONG'].includes(element.tagName)) {
      styles.push('BOLD');
    }
    if (style.fontStyle || ['I', 'EM'].includes(element.tagName)) {
      styles.push('ITALIC');
    }
    if (style.textDecoration || ['S', 'U', 'DEL'].includes(element.tagName)) {
      if (style.textDecoration.includes('line-through') || ['S', 'DEL'].includes(element.tagName)) {
        styles.push('STRIKETHROUGH');
      }
      if (style.textDecoration.includes('underline') || element.tagName === 'U') {
        styles.push('UNDERLINE');
      }
    }

    return styles;
  };

  const traverse = (node, block, inlineStyles = [], depth = 0) => {
    if (node?.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      const start = block.text.length;
      block.text += text;
      inlineStyles.forEach((style) => {
        block.inlineStyleRanges.push({
          offset: start,
          length: text.length,
          style,
        });
      });
    } else if (node?.nodeType === Node.ELEMENT_NODE) {
      const newInlineStyles = [...inlineStyles, ...getInlineStyles(node)];
      switch (node.tagName) {
        case 'BR':
          block.text += '\n';
          return;
        case 'UL':
        case 'OL':
          Array.from(node.childNodes).forEach((child) => {
            const listItemBlock = {
              text: '',
              type: node.tagName === 'UL' ? 'unordered-list-item' : 'ordered-list-item',
              depth,
              inlineStyleRanges: [],
              entityRanges: [],
              data: {},
            };
            if (!['OL', 'UL'].includes(child.tagName)) {
              rawContentState.blocks.push(listItemBlock);
            }
            traverse(child, listItemBlock, newInlineStyles, depth + 1);
          });
          return;
        case 'LI':
          traverse(node.firstChild, block, newInlineStyles, depth);
          return;
        case 'DIV': {
          if (node.innerHTML.includes('&nbsp;')) {
            const count = (node.innerHTML.match(/&nbsp;/g) || []).length;
            block.type = `IndentBlock${count}`;
          }

          switch (node.style.textAlign) {
            case 'center':
              block.type = 'CenterAlignedBlock';
              break;
            case 'left':
              block.type = 'LeftAlignedBlock';
              break;
            case 'right':
              block.type = 'RightAlignedBlock';
              break;
            case 'justify':
              block.type = 'JustifiedBlock';
              break;
            default:
              break;
          }
          rawContentState.blocks.push(block);
          Array.from(node.childNodes).forEach((child) => {
            traverse(child, block, newInlineStyles, depth);
          });
          return;
        }
        default:
          break;
      }
      Array.from(node.childNodes).forEach((child) => {
        traverse(child, block, newInlineStyles, depth);
      });
    }
  };

  Array.from(doc.body.childNodes).forEach((child) => {
    const rootBlock = {
      text: '',
      type: 'unstyled',
      depth: 0,
      inlineStyleRanges: [],
      entityRanges: [],
      data: {},
    };
    traverse(child, rootBlock);
    if (rootBlock.text.length && !rawContentState.blocks.length && !isBlockTag) {
      rawContentState.blocks.push(rootBlock);
    }
  });
  return rawContentState;
};

export const convertRawContentStateToHtml = (rawContentState) => {
  const applyInlineStyles = (text, inlineStyleRanges) => {
    const styleMap = {};
    inlineStyleRanges.forEach((range) => {
      const { offset, length, style } = range;
      for (let i = offset; i < offset + length; i++) {
        if (!styleMap[i]) {
          styleMap[i] = new Set();
        }
        styleMap[i].add(style);
      }
    });

    let styledText = '';
    let currentStyles = new Set();

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const newStyles = styleMap[i] || new Set();

      if (JSON.stringify([...currentStyles]) !== JSON.stringify([...newStyles])) {
        if (currentStyles.size > 0) {
          styledText += '</span>';
        }
        if (newStyles.size > 0) {
          const styleobject = [...newStyles].reduce((acc, style) => {
            let styleValue;
            if (style.startsWith('color-')) {
              styleValue = style.split('-')[1];
              acc.color = `color: ${styleValue}`;
            } else if (style.startsWith('backgroundColor-')) {
              styleValue = style.split('-')[1];
              acc.backgroundColor = `background-color: ${styleValue}`;
            } else if (style.startsWith('fontFamily-')) {
              acc.fontFamily = `font-family: ${style.split('-')[1]}`;
            } else if (style.startsWith('fontSize-')) {
              acc.fontSize = `font-size: ${style.split('-')[1]}`;
            } else if (style.startsWith('LOWERCASE')) {
              acc.textTransform = 'text-transform: lowercase';
            } else if (style.startsWith('UPPERCASE')) {
              acc.textTransform = 'text-transform: uppercase';
            } else if (style.startsWith('BOLD')) {
              acc.fontWeight = 'font-weight: bold';
            } else if (style.startsWith('ITALIC')) {
              acc.fontStyle = 'font-style: italic';
            } else if (style.startsWith('UNDERLINE')) {
              const textDecorationValue = (acc.textDecoration || '')
                .replace('text-decoration: ', '')
                .trim();
              acc.textDecoration = `text-decoration: ${textDecorationValue} underline`;
            } else if (style.startsWith('STRIKETHROUGH')) {
              const textDecorationValue = (acc.textDecoration || '')
                .replace('text-decoration: ', '')
                .trim();
              acc.textDecoration = `text-decoration: ${textDecorationValue} line-through`;
            }
            return acc;
          }, {});

          const styleString = Object.values(styleobject).join('; ');

          styledText += `<span style="${styleString}">`;
        }
        currentStyles = newStyles;
      }
      styledText += char;
    }

    if (currentStyles.size > 0) {
      styledText += '</span>';
    }

    return styledText;
  };

  const convertBlock = (block) => {
    let blockHtml = applyInlineStyles(block.text, block.inlineStyleRanges);
    blockHtml = blockHtml.replace(/\n/g, '<br>');

    switch (block.type) {
      case 'LeftAlignedBlock':
        return `<div style="text-align: left">${blockHtml}</div>`;
      case 'CenterAlignedBlock':
        return `<div style="text-align: center">${blockHtml}</div>`;
      case 'RightAlignedBlock':
        return `<div style="text-align: right">${blockHtml}</div>`;
      case 'JustifiedBlock':
        return `<div style="text-align: justify">${blockHtml}</div>`;
      case 'unordered-list-item':
      case 'ordered-list-item':
        return `<li>${blockHtml}</li>`;
      case 'unstyled':
        return `<div>${blockHtml}</div>`;
      default: {
        if (block.type.includes('IndentBlock')) {
          const insertedTabs = Number(block.type.replace('IndentBlock', ''));
          return `<div>${'&nbsp'.repeat(insertedTabs >= 10 ? 10 : insertedTabs)}${blockHtml}</div>`;
        }
        return blockHtml;
      }
    }
  };

  const blocksHtml = rawContentState.blocks.map(convertBlock);

  const depthArr = rawContentState.blocks.map((block) => block.depth);

  const listHtml = blocksHtml.reduce((acc, blockHtml, index, array) => {
    if (blockHtml.startsWith('<li>')) {
      const prevBlock = array[index - 1] || '';
      const nextBlock = array[index + 1] || '';
      const blockHtmlType = rawContentState.blocks[index].type;
      const prevBlockType = rawContentState.blocks[index - 1]?.type || '';
      const nextBlockType = rawContentState.blocks[index + 1]?.type || '';
      if (
        !prevBlock.startsWith('<li>') ||
        (prevBlock.startsWith('<li>') && blockHtmlType !== prevBlockType) ||
        (prevBlock.startsWith('<li>') && depthArr[index] !== depthArr[index - 1])
      ) {
        const listTag = blockHtmlType === 'ordered-list-item' ? '<ol>' : '<ul>';

        if (depthArr[index] > depthArr[index - 1]) {
          acc += listTag.repeat(Math.abs(depthArr[index] - depthArr[index - 1]));
        } else if (!depthArr[index - 1]) {
          acc += listTag.repeat(Math.abs(depthArr[index] + 1));
        }
      }
      acc += blockHtml;

      if (
        !nextBlock.startsWith('<li>') ||
        (nextBlock.startsWith('<li>') && blockHtmlType !== nextBlockType) ||
        (prevBlock.startsWith('<li>') && depthArr[index] !== depthArr[index + 1])
      ) {
        const listTag = blockHtmlType === 'ordered-list-item' ? '</ol>' : '</ul>';

        if (depthArr[index] > depthArr[index + 1]) {
          acc += listTag.repeat(Math.abs(depthArr[index] - depthArr[index + 1] + 1));
        } else if (!depthArr[index + 1]) {
          acc += listTag.repeat(Math.abs(depthArr[index] + 1));
        }
      }
    } else {
      acc += blockHtml;
    }
    return acc;
  }, '');
  return listHtml;
};

export const rgbToHex = (rgb) => {
  const result = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/.exec(rgb);
  return result
    ? `#${(
        (1 << 24) +
        (parseInt(result[1]) << 16) +
        (parseInt(result[2]) << 8) +
        parseInt(result[3])
      )
        .toString(16)
        .slice(1)}`
    : rgb;
};

export const checkBlockTag = (str) => {
  const htmlPattern = /^<(?!b>|i>|s>|u>|p>|span(\s|>))[^>]+>/;
  return htmlPattern.test(str);
};

export const contentHasText = (state) => {
  const blocks = state?.blocks ?? [];
  const blocksWithText = blocks.filter((block) => block?.text?.length);
  return blocksWithText.length > 0;
};
